import {
  Alert,
  Button,
  Card,
  Select,
  Spin,
  Table,
  Tag,
  Tooltip,
  message,
  notification,
} from 'antd'
import {
  CopyOutlined,
  InfoCircleOutlined,
  MailOutlined,
} from '@ant-design/icons'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  fetchCrowsNestOpportunities,
  patchCrowsNestOpportunity,
} from 'src/utils/crowsNestApi'
import {
  CROWS_NEST_FEED_POLL_MS,
  buildOpportunityCelebrationToast,
  resolveNewOpportunitiesForCelebration,
} from 'src/utils/crowsNestCelebration'
import {
  CROWS_NEST_FEED_HANDOFF_FILTERS,
  CROWS_NEST_FEED_PAGE_SIZE,
  CROWS_NEST_FEED_TYPE_FILTERS,
  buildOpportunityFeedHandoffQuery,
  mapOpportunityFeedItem,
  mapOpportunityFeedResponse,
} from 'src/utils/crowsNestOpportunityFeed'
import { fireCrowsNestConfetti } from 'src/utils/crowsNestConfetti'
import { CROWS_NEST_TOOLTIPS } from 'src/utils/crowsNestTooltips'
import { copyTextToClipboard } from 'src/utils/spartacusClipboard'
import {
  formatSpartacusPhoneLabel,
  getSpartacusPhoneHref,
} from 'src/utils/spartacusMappers'
import { openVenueDashboard } from 'src/utils/spartacusVenueActions'
import { crowsNestErrorMessage } from 'src/utils/crowsNestErrors'
import styled, { keyframes } from 'styled-components/macro'
import { stickyActionsColumn } from 'src/components/styled/horizontalScroll'
import CrowsNestOpportunityExpand from './CrowsNestOpportunityExpand'
import CrowsNestTipLabel from './CrowsNestTipLabel'

const { Option } = Select

const HIGHLIGHT_MS = 8000

/**
 * Sales opportunity feed — venue, feature, contact, suggested follow-up.
 * Light toast + confetti when a brand-new opportunity id appears.
 */
const CrowsNestOpportunityFeed = ({ onCreateDeal } = {}) => {
  const accountId = useSelector(state => state.auth?.account?._id)
  const [typeFilter, setTypeFilter] = useState('')
  const [handoffFilter, setHandoffFilter] = useState('')
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [rows, setRows] = useState([])
  const [highlightedIds, setHighlightedIds] = useState(() => new Set())
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: CROWS_NEST_FEED_PAGE_SIZE,
    total: 0,
  })
  const [totals, setTotals] = useState(null)
  const highlightTimerRef = useRef(null)
  const cancelConfettiRef = useRef(null)
  const [statusSavingId, setStatusSavingId] = useState(null)

  const mergeUpdatedRow = useCallback(updated => {
    if (!updated?.id) return
    setRows(prev =>
      prev.map(row => (row.id === updated.id ? { ...row, ...updated } : row)),
    )
  }, [])

  const setOpportunityStatus = useCallback(
    async (row, nextStatus) => {
      if (!row?.id || row.status === nextStatus) return
      setStatusSavingId(row.id)
      try {
        const response = await patchCrowsNestOpportunity(row.id, {
          status: nextStatus,
        })
        const mapped = mapOpportunityFeedItem(response.opportunity)
        if (mapped) mergeUpdatedRow(mapped)
        message.success(
          nextStatus === 'contacted'
            ? 'Marked contacted'
            : 'Moved back to open',
        )
      } catch (err) {
        message.error(
          crowsNestErrorMessage(err, "Couldn't update the status. Try again.", {
            range: false,
          }),
        )
      } finally {
        setStatusSavingId(null)
      }
    },
    [mergeUpdatedRow],
  )

  const celebrateNewOpportunities = useCallback(opportunities => {
    const { newOpportunities } =
      resolveNewOpportunitiesForCelebration(opportunities)
    if (!newOpportunities.length) {
      return
    }

    const toast = buildOpportunityCelebrationToast(newOpportunities)
    if (toast) {
      notification.success({
        message: toast.message,
        description: toast.description,
        duration: 4.5,
        placement: 'topRight',
      })
    }

    if (cancelConfettiRef.current) {
      cancelConfettiRef.current()
    }
    cancelConfettiRef.current = fireCrowsNestConfetti()

    const ids = new Set(newOpportunities.map(row => String(row.id)))
    setHighlightedIds(ids)
    if (highlightTimerRef.current) {
      clearTimeout(highlightTimerRef.current)
    }
    highlightTimerRef.current = setTimeout(() => {
      setHighlightedIds(new Set())
      highlightTimerRef.current = null
    }, HIGHLIGHT_MS)
  }, [])

  const loadFeed = useCallback(
    async ({ page: nextPage, type, handoff, quiet = false } = {}) => {
      if (!quiet) {
        setLoading(true)
      }
      setError(null)
      try {
        const query = {
          page: nextPage,
          pageSize: CROWS_NEST_FEED_PAGE_SIZE,
          ...buildOpportunityFeedHandoffQuery(handoff, accountId),
        }
        if (type) query.type = type

        const response = await fetchCrowsNestOpportunities(query)
        const mapped = mapOpportunityFeedResponse(response)
        setRows(mapped.opportunities)
        setTotals(mapped.totals)
        setPagination({
          page: mapped.pagination.page || nextPage,
          pageSize: mapped.pagination.pageSize || CROWS_NEST_FEED_PAGE_SIZE,
          total: mapped.pagination.total || 0,
        })
        // Only celebrate from page 1 so pagination does not re-fire noise.
        if (nextPage === 1) {
          celebrateNewOpportunities(mapped.opportunities)
        }
      } catch (err) {
        setError(
          crowsNestErrorMessage(
            err,
            "Couldn't load the opportunity feed. Try again in a moment.",
          ),
        )
        if (!quiet) {
          setRows([])
        }
      } finally {
        if (!quiet) {
          setLoading(false)
        }
      }
    },
    [accountId, celebrateNewOpportunities],
  )

  useEffect(() => {
    loadFeed({ page, type: typeFilter, handoff: handoffFilter })
  }, [loadFeed, page, typeFilter, handoffFilter])

  useEffect(() => {
    const timer = setInterval(() => {
      loadFeed({
        page,
        type: typeFilter,
        handoff: handoffFilter,
        quiet: true,
      })
    }, CROWS_NEST_FEED_POLL_MS)
    return () => clearInterval(timer)
  }, [loadFeed, page, typeFilter, handoffFilter])

  useEffect(
    () => () => {
      if (highlightTimerRef.current) {
        clearTimeout(highlightTimerRef.current)
      }
      if (cancelConfettiRef.current) {
        cancelConfettiRef.current()
      }
    },
    [],
  )

  const copyOpportunityContext = useCallback(async row => {
    try {
      await copyTextToClipboard(row.copyText || '')
      message.success('Venue context copied')
    } catch (err) {
      message.error(err.message || 'Could not copy')
    }
  }, [])

  const copyOpportunityEmail = useCallback(async row => {
    const text = row.outreachCopyText || row.contactEmail
    if (!text) {
      message.error('No contact email')
      return
    }
    try {
      await copyTextToClipboard(text)
      message.success('Email draft copied — paste into your mail client')
    } catch (err) {
      message.error(err.message || 'Could not copy')
    }
  }, [])

  const copyContactEmail = useCallback(async email => {
    const value = String(email || '').trim()
    if (!value) return
    try {
      await copyTextToClipboard(value)
      message.success('Email address copied')
    } catch (err) {
      message.error(err.message || 'Could not copy')
    }
  }, [])

  const columns = useMemo(
    () => [
      {
        title: (
          <CrowsNestTipLabel label='When' tip={CROWS_NEST_TOOLTIPS.ageBadge} />
        ),
        key: 'when',
        width: 160,
        render: (_value, row) => (
          <WhenCell>
            <span>{row.occurredAtLabel}</span>
            {row.ageLabel ? (
              <AgeBadge
                $urgency={row.ageUrgency}
                data-testid={`opportunity-age-${row.id}`}
              >
                {row.ageLabel}
              </AgeBadge>
            ) : null}
          </WhenCell>
        ),
      },
      {
        title: 'Venue',
        key: 'venue',
        render: (_value, row) => (
          <VenueCell>
            <VenueButton
              type='link'
              onClick={() => openVenueDashboard(row.venueId)}
              disabled={!row.venueId}
            >
              {row.venueName}
            </VenueButton>
            <Meta>{row.plan}</Meta>
          </VenueCell>
        ),
      },
      {
        title: (
          <CrowsNestTipLabel label='Signal' tip={CROWS_NEST_TOOLTIPS.signal} />
        ),
        key: 'signal',
        render: (_value, row) => (
          <SignalCell>
            <Tag>{row.typeLabel}</Tag>
            <Meta>{row.featureLabel}</Meta>
          </SignalCell>
        ),
      },
      {
        title: (
          <CrowsNestTipLabel label='Status' tip={CROWS_NEST_TOOLTIPS.status} />
        ),
        key: 'status',
        width: 130,
        render: (_value, row) => {
          const isContacted = row.status === 'contacted'
          return (
            <StatusChip
              color={isContacted ? 'green' : 'orange'}
              onClick={() =>
                setOpportunityStatus(row, isContacted ? 'open' : 'contacted')
              }
              data-testid={`opportunity-status-${row.id}`}
            >
              {statusSavingId === row.id
                ? 'Saving…'
                : row.statusLabel || (isContacted ? 'Contacted' : 'Open')}
            </StatusChip>
          )
        },
      },
      {
        title: (
          <CrowsNestTipLabel label='Owner' tip={CROWS_NEST_TOOLTIPS.owner} />
        ),
        key: 'owner',
        width: 150,
        render: (_value, row) => (
          <OwnerCell>
            <strong>
              {row.lastTouchedByLabel && row.lastTouchedByLabel !== '—'
                ? row.lastTouchedByLabel
                : '—'}
            </strong>
            {row.lastTouchedAtLabel && row.lastTouchedAtLabel !== '—' ? (
              <Meta>{row.lastTouchedAtLabel}</Meta>
            ) : null}
          </OwnerCell>
        ),
      },
      {
        title: 'Contact',
        key: 'contact',
        render: (_value, row) => {
          const phoneHref = getSpartacusPhoneHref(row.contactPhone)
          return (
            <ContactCell>
              <strong>{row.contactLabel}</strong>
              {row.contactEmail ? (
                <ContactCopyButton
                  type='link'
                  size='small'
                  onClick={() => copyContactEmail(row.contactEmail)}
                  data-testid={`opportunity-contact-email-${row.id}`}
                  title='Copy email address'
                >
                  {row.contactEmail}
                </ContactCopyButton>
              ) : null}
              {phoneHref ? (
                <ContactLink href={phoneHref}>
                  {formatSpartacusPhoneLabel(row.contactPhone)}
                </ContactLink>
              ) : null}
            </ContactCell>
          )
        },
      },
      {
        title: (
          <CrowsNestTipLabel
            label='Actions'
            tip={CROWS_NEST_TOOLTIPS.actions}
          />
        ),
        key: 'actions',
        className: 'sticky-actions-col',
        onCell: () => ({ className: 'sticky-actions-cell' }),
        width: onCreateDeal ? 200 : 110,
        render: (_value, row) => {
          const canCopyEmail = Boolean(row.outreachCopyText || row.contactEmail)
          return (
            <ActionsCell>
              {onCreateDeal ? (
                <Button
                  size='small'
                  type='primary'
                  onClick={() => onCreateDeal(row)}
                  data-testid={`opportunity-create-deal-${row.id}`}
                >
                  Create deal
                </Button>
              ) : null}
              <Tooltip
                title={canCopyEmail ? 'Copy email draft' : 'No contact email'}
              >
                <ActionButton
                  type='link'
                  size='small'
                  disabled={!canCopyEmail}
                  onClick={() => copyOpportunityEmail(row)}
                  data-testid={`opportunity-mail-${row.id}`}
                  aria-label='Copy email draft'
                >
                  <MailOutlined />
                </ActionButton>
              </Tooltip>
              <Tooltip title='Copy venue + context'>
                <ActionButton
                  type='link'
                  size='small'
                  onClick={() => copyOpportunityContext(row)}
                  data-testid={`opportunity-copy-${row.id}`}
                  aria-label='Copy venue context'
                >
                  <CopyOutlined />
                </ActionButton>
              </Tooltip>
            </ActionsCell>
          )
        },
      },
    ],
    [
      copyContactEmail,
      copyOpportunityContext,
      copyOpportunityEmail,
      onCreateDeal,
      setOpportunityStatus,
      statusSavingId,
    ],
  )

  return (
    <FeedCard
      data-testid='crows-nest-opportunity-feed'
      title={
        <TitleBlock>
          <TitleRow>
            <span>Opportunity feed</span>
            <Tooltip title={CROWS_NEST_TOOLTIPS.opportunityFeed}>
              <InfoCircleOutlined />
            </Tooltip>
          </TitleRow>
          {totals?.mixLabel ? (
            <MixLabel data-testid='opportunity-mix-label'>
              <CrowsNestTipLabel
                label={totals.mixLabel}
                tip={CROWS_NEST_TOOLTIPS.opportunityMix}
              />
            </MixLabel>
          ) : null}
        </TitleBlock>
      }
      extra={
        <FilterRow>
          <Tooltip title={CROWS_NEST_TOOLTIPS.typeFilter}>
            <FilterTipIcon aria-label={CROWS_NEST_TOOLTIPS.typeFilter} />
          </Tooltip>
          <Select
            value={typeFilter}
            onChange={value => {
              setPage(1)
              setTypeFilter(value)
            }}
            style={{ width: 180 }}
            data-testid='opportunity-type-filter'
          >
            {CROWS_NEST_FEED_TYPE_FILTERS.map(option => (
              <Option key={option.value || 'all'} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
          <Tooltip title={CROWS_NEST_TOOLTIPS.handoffFilter}>
            <FilterTipIcon aria-label={CROWS_NEST_TOOLTIPS.handoffFilter} />
          </Tooltip>
          <Select
            value={handoffFilter}
            onChange={value => {
              setPage(1)
              setHandoffFilter(value)
            }}
            style={{ width: 140 }}
            data-testid='opportunity-handoff-filter'
          >
            {CROWS_NEST_FEED_HANDOFF_FILTERS.map(option => (
              <Option key={option.value || 'all-leads'} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </FilterRow>
      }
    >
      {error && !rows.length ? (
        <Alert type='error' showIcon message={error} />
      ) : null}

      {loading && !rows.length ? (
        <LoadingWrap>
          <Spin />
        </LoadingWrap>
      ) : (
        <Table
          rowKey='key'
          columns={columns}
          dataSource={rows}
          loading={loading}
          rowClassName={row =>
            highlightedIds.has(String(row.id)) ? 'crows-nest-new-lead' : ''
          }
          expandedRowRender={row => (
            <CrowsNestOpportunityExpand row={row} onUpdated={mergeUpdatedRow} />
          )}
          pagination={{
            current: pagination.page,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: false,
            onChange: nextPage => setPage(nextPage),
          }}
          locale={{ emptyText: 'No opportunities in this range yet.' }}
          scroll={{ x: true }}
        />
      )}
    </FeedCard>
  )
}

const newLeadPulse = keyframes`
  0% {
    background-color: rgba(124, 179, 66, 0.28);
  }
  100% {
    background-color: rgba(124, 179, 66, 0.08);
  }
`

const FeedCard = styled(Card)`
  ${stickyActionsColumn}
  .ant-card-head-title {
    font-weight: 600;
  }

  .ant-table-tbody > tr.crows-nest-new-lead > td {
    animation: ${newLeadPulse} 1.2s ease-in-out 3;
    background-color: rgba(124, 179, 66, 0.12);
  }
`

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const TitleRow = styled.span`
  align-items: center;
  display: inline-flex;
  gap: 8px;

  .anticon {
    color: ${({ theme }) => theme.colours.oliveGrey || '#888'};
    font-size: 14px;
  }
`

const MixLabel = styled.span`
  color: ${({ theme }) => theme.colours.oliveGrey || '#666'};
  font-size: 12px;
  font-weight: 500;
`

const FilterRow = styled.div`
  align-items: center;
  display: inline-flex;
  gap: 8px;
`

const FilterTipIcon = styled(InfoCircleOutlined)`
  color: ${({ theme }) => theme.colours.oliveGrey || '#888'};
  font-size: 14px;
`

const LoadingWrap = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  min-height: 120px;
`

const VenueCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const WhenCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const AgeBadge = styled.span`
  color: ${({ $urgency, theme }) => {
    if ($urgency === 'today') return theme.colours.tomatoRed || '#b33a3a'
    if ($urgency === 'recent') return theme.colours.oliveGrey || '#666'
    return theme.colours.oliveGrey || '#888'
  }};
  font-size: 11px;
  font-weight: ${({ $urgency }) => ($urgency === 'today' ? 700 : 600)};
  letter-spacing: 0.01em;
  text-transform: uppercase;
`

const VenueButton = styled(Button)`
  && {
    height: auto;
    padding: 0;
    text-align: left;
    white-space: normal;
  }
`

const SignalCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`

const Meta = styled.span`
  color: ${({ theme }) => theme.colours.oliveGrey || '#666'};
  font-size: 12px;
`

const StatusChip = styled(Tag)`
  && {
    cursor: pointer;
    margin: 0;
    user-select: none;
  }
`

const OwnerCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const ContactCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const ContactLink = styled.a`
  color: ${({ theme }) => theme.colours.bbqBlack || '#1a1a1a'};
  font-size: 12px;

  &:hover {
    color: ${({ theme }) => theme.colours.lettuceGreen || '#5a8f3a'};
  }
`

const ContactCopyButton = styled(Button)`
  && {
    color: ${({ theme }) => theme.colours.bbqBlack || '#1a1a1a'};
    font-size: 12px;
    height: auto;
    padding: 0;
    text-align: left;
    white-space: normal;

    &:hover {
      color: ${({ theme }) => theme.colours.lettuceGreen || '#5a8f3a'};
    }
  }
`

const ActionsCell = styled.div`
  align-items: center;
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
`

const ActionButton = styled(Button)`
  && {
    color: ${({ theme }) => theme.colours.oliveGrey || '#666'};
    height: 28px;
    padding: 0 6px;
    width: 28px;

    &:hover:not(:disabled) {
      color: ${({ theme }) => theme.colours.lettuceGreen || '#5a8f3a'};
    }
  }
`

export default CrowsNestOpportunityFeed
